import React, { FunctionComponent, useEffect, useState } from 'react';

import './labeled-input.scss';

type CommonProps = {
    id?: string; // required, for <label htmlFor>
    label: string;
    error?: string;
    hint?: string;
    required?: boolean;
    className?: string;
};

/* -------- Input -------- */

type NativeInput = React.InputHTMLAttributes<HTMLInputElement>;
export type LabeledInputProps = Omit<NativeInput, keyof CommonProps | 'value' | 'onChange'> &
    CommonProps & {
        value?: string | number;
        onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    };

export const LegacyLabeledInput: FunctionComponent<LabeledInputProps> = props => {
    const { label, error, onChange, value: initialValue, ...inputProps } = props;
    const [value, setValue] = useState<string | number | undefined>(initialValue);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(e.target.value);
        if (onChange) {
            onChange(e);
        }
    };

    return (
        <label className={`LabeledInput ${props.type} ${error ? 'error' : ''}`}>
            {!!error && <div className="error">{error}</div>}
            <input {...inputProps} value={value} onChange={handleChange} />
            <span className="label">{label}</span>
        </label>
    );
};

export const LabeledInput: FunctionComponent<LabeledInputProps> = ({
    id,
    label,
    error,
    hint,
    required,
    className,
    value: controlled,
    onChange,
    ...rest
}) => {
    const [value, setValue] = useState<string | number | undefined>(controlled);
    useEffect(() => {
        setValue(controlled);
    }, [controlled]);

    const describedBy = [hint && `${id}-hint`, error && `${id}-error`].filter(Boolean).join(' ') || undefined;

    return (
        <div className={`Field ${error ? 'is-error' : ''} ${className || ''}`}>
            <label htmlFor={id} className="Field-label">
                {label}
                {required && (
                    <span className="Field-required" aria-hidden>
                        *
                    </span>
                )}
            </label>

            <div className="Field-control">
                <input
                    id="{id}"
                    className="Field-input"
                    aria-invalid={!!error}
                    aria-describedby={describedBy}
                    value={value ?? ''}
                    onChange={e => {
                        setValue(e.target.value);
                        onChange && onChange(e);
                    }}
                    required={required}
                    {...rest}
                />
            </div>

            {hint && !error && (
                <div id={`${id}-hint`} className="Field-hint">
                    {hint}
                </div>
            )}
            {error && (
                <div id={`${id}-error`} className="Field-error">
                    {error}
                </div>
            )}
        </div>
    );
};

/* -------- Textarea -------- */

type NativeTextarea = React.TextareaHTMLAttributes<HTMLTextAreaElement>;
export type LabeledTextAreaProps = Omit<NativeTextarea, keyof CommonProps | 'value' | 'onChange'> &
    CommonProps & {
        value?: string;
        onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    };

export const LabeledTextArea: FunctionComponent<LabeledTextAreaProps> = ({
    id,
    label,
    error,
    hint,
    required,
    className,
    value: controlled,
    onChange,
    rows = 6,
    ...rest
}) => {
    const [value, setValue] = useState<string | undefined>(controlled);
    useEffect(() => {
        setValue(controlled);
    }, [controlled]);

    const describedBy = [hint && `${id}-hint`, error && `${id}-error`].filter(Boolean).join(' ') || undefined;

    return (
        <div className={`Field ${error ? 'is-error' : ''} ${className || ''}`}>
            <label htmlFor={id} className="Field-label">
                {label}
                {required && (
                    <span className="Field-required" aria-hidden>
                        *
                    </span>
                )}
            </label>

            <div className="Field-control">
                <textarea
                    id={id}
                    className="Field-textarea"
                    aria-invalid={!!error}
                    aria-describedby={describedBy}
                    value={value ?? ''}
                    onChange={e => {
                        setValue(e.target.value);
                        onChange && onChange(e);
                    }}
                    rows={rows}
                    required={required}
                    {...rest}
                />
            </div>

            {hint && !error && (
                <div id={`${id}-hint`} className="Field-hint">
                    {hint}
                </div>
            )}
            {error && (
                <div id={`${id}-error`} className="Field-error">
                    {error}
                </div>
            )}
        </div>
    );
};
